import { useCallback, useEffect, useRef, useState, type AnchorHTMLAttributes, type ReactNode } from "react";
import { APP_BASE, APP_HEADER, underAppBase } from "./app";
import { useAuth } from "./auth";
import { useApi } from "./index";
import type { SessionResponse } from "./types";

/**
 * Cross-surface session handoff. The staff apps (AI Workspace, Case Management, Platform Console) each keep their own session, on hosts of
 * their own or under base paths of one host (the back office at the root of ncp.moet.gov.ae, the console under /admin), so a deep link from
 * one to another carries a single-use `?handoff=` code (60 s, minted by `POST /api/auth/handoff`) that the target app redeems on load
 * instead of showing its sign-in page.
 */
export const HANDOFF_PARAM = "handoff";

export interface HandoffCode {
  code: string;
  expiresAt: string;
}

function readHandoffCode(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(HANDOFF_PARAM);
}

function stripHandoffParam(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(HANDOFF_PARAM)) return;
  url.searchParams.delete(HANDOFF_PARAM);
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  // The data router was created (and read the location) before this ran; a synthetic popstate makes it pick up the clean URL.
  window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
}

/**
 * Redeems a `?handoff=` code before anything below it renders, so route guards never see the signed-out state in between. A code that
 * fails (expired, already used) is dropped silently and the app continues with whatever session it had, usually its sign-in page.
 */
export function HandoffGate({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const api = useApi();
  const { setSession } = useAuth();
  const [code] = useState(readHandoffCode);
  const [pending, setPending] = useState(code !== null);
  const started = useRef(false);

  useEffect(() => {
    if (!code || started.current) return;
    started.current = true;
    void (async () => {
      try {
        // Plain fetch: a rejected code must not trigger the client's 401 handler, which would also drop an existing session here.
        const res = await fetch(`${api.baseUrl}/api/auth/handoff/redeem`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", [APP_HEADER]: APP_BASE },
          body: JSON.stringify({ code }),
        });
        // The redeemed session is a child of the source one (same sign-in time and 10-hour limit), with its own refresh cookie here.
        if (res.ok) setSession((await res.json()) as SessionResponse);
      } catch {
        // Network failure: fall through to the normal sign-in.
      } finally {
        stripHandoffParam();
        setPending(false);
      }
    })();
  }, [api, code, setSession]);

  return <>{pending ? fallback : children}</>;
}

/**
 * Whether a URL may be another app: another host, or this host outside this app's base path. From an app at the root of a host (the back
 * office) a path may still be an app below it (the console under /admin): the API decides, and answers `handoff_same_app` for its own pages.
 */
function mayBeOtherApp(url: string): boolean {
  try {
    const u = new URL(url, window.location.href);
    return !underAppBase(u) || APP_BASE === "/";
  } catch {
    return false;
  }
}

function withParam(url: string, code: string): string {
  const u = new URL(url, window.location.href);
  u.searchParams.set(HANDOFF_PARAM, code);
  return u.toString();
}

/**
 * Opens (or navigates to) another staff surface with the current session. Links within this app and signed-out users get the plain URL;
 * if the server refuses to mint a code (the target is this app, or not a registered staff surface) the plain URL is used as well.
 */
export function useHandoff() {
  const api = useApi();
  const { isAuthenticated, user } = useAuth();
  const eligible = isAuthenticated && user?.kind === "Staff";

  const resolve = useCallback(
    async (url: string): Promise<string> => {
      if (!eligible || !mayBeOtherApp(url)) return url;
      try {
        const { code } = await api.post<HandoffCode>("/api/auth/handoff", { target: new URL(url, window.location.href).toString() });
        return withParam(url, code);
      } catch {
        return url;
      }
    },
    [api, eligible],
  );

  /**
   * New tab. The window is opened inside the click so pop-up blockers allow it, then pointed at the URL once the code is minted.
   * A promised URL (looked up after the click) gets the same treatment; when it settles on nothing the blank tab is closed again.
   */
  const open = useCallback(
    (target: string | Promise<string | null | undefined>) => {
      if (typeof target !== "string") {
        const win = window.open("", "_blank");
        if (win) win.opener = null;
        void target
          .then(async (url) => {
            if (!url) { win?.close(); return; }
            const href = await resolve(url);
            if (win) win.location.href = href;
            else window.open(href, "_blank", "noopener");
          })
          .catch(() => win?.close());
        return;
      }
      const url = target;
      if (!eligible || !mayBeOtherApp(url)) {
        window.open(url, "_blank", "noopener");
        return;
      }
      const win = window.open("", "_blank");
      if (!win) {
        window.open(url, "_blank", "noopener");
        return;
      }
      win.opener = null;
      void resolve(url).then((href) => {
        win.location.href = href;
      });
    },
    [eligible, resolve],
  );

  /** Same tab, for redirects (a moved page forwarding to the console, for example). */
  const navigate = useCallback(
    async (url: string, options?: { replace?: boolean }) => {
      const href = await resolve(url);
      if (options?.replace) window.location.replace(href);
      else window.location.assign(href);
    },
    [resolve],
  );

  return { open, navigate, resolve };
}

/** `<a target="_blank">` to another staff surface that carries the session. Modified clicks keep the browser's default behaviour. */
export function HandoffLink({ href, onClick, target = "_blank", rel = "noopener", ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const { open } = useHandoff();
  return (
    <a
      href={href}
      target={target}
      rel={rel}
      {...rest}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        open(href);
      }}
    />
  );
}
